import React, { useEffect, useState } from 'react';
import styles from '../Styles/Campaigns.module.css';
import {
  ANCHORS,
  ANCHOR_LABEL,
  RANGE,
  SLOT_KINDS,
  SLOT_KIND_LABEL,
  newSlot,
  parseCount,
  slotError,
  slotLabel,
  withUnit,
} from '../campaignSlots';

// SCRUM-449: a playbook campaign's "When" — its list of schedule slots.
// A slot is either "every N days at a time" or "N days (at a time) / N hours
// before or after one of the event's dates".

export default function SlotEditor({ slots, onChange, onValidityChange }) {
  const [addKind, setAddKind] = useState('repeat');
  // A number as typed, per slot id, while it is not valid — the slot itself
  // keeps its last valid value; Save stays off until it is fixed.
  const [raw, setRaw] = useState({});
  const errors = Object.fromEntries(
    slots
      .map((s) => {
        const typed = raw[s.id];
        const field = s.kind === 'repeat' ? 'everyDays' : s.unit;
        const typedError = typed !== undefined ? parseCount(typed, field).error : undefined;
        return [s.id, typedError ?? slotError(s)];
      })
      .filter(([, err]) => err)
  );
  const emptyError = slots.length === 0 ? 'Add at least one slot.' : null;
  const invalid = Object.keys(errors).length > 0 || !!emptyError;
  useEffect(() => {
    onValidityChange?.(invalid);
  }, [invalid, onValidityChange]);

  const clearRaw = (id) => setRaw(({ [id]: _, ...rest }) => rest);
  const replace = (i, next) => onChange(slots.map((s, j) => (j === i ? next : s)));
  const patch = (i, p) => replace(i, { ...slots[i], ...p });
  const changeCount = (i, key, field, typed) => {
    const s = slots[i];
    const { value } = parseCount(typed, field);
    if (value === undefined) {
      setRaw((r) => ({ ...r, [s.id]: typed }));
      return;
    }
    clearRaw(s.id);
    patch(i, { [key]: value });
  };
  const changeKind = (i, kind) => {
    clearRaw(slots[i].id);
    const fresh = newSlot(kind, slots.filter((_, j) => j !== i));
    replace(i, { ...fresh, id: slots[i].id });
  };
  const changeUnit = (i, unit) => {
    clearRaw(slots[i].id);
    replace(i, withUnit(slots[i], unit));
  };
  const timeInput = (s, i) => (
    <label className={styles.inlineLabel}>
      at
      <input type="time" aria-label="At" value={s.at ?? ''} onChange={(e) => patch(i, { at: e.target.value })} />
    </label>
  );

  return (
    <div className={styles.slotList}>
      {slots.map((s, i) => (
        <div key={s.id} className={styles.slotRow}>
          <span className={styles.slotChip}>{slotLabel(s)}</span>
          <div className={styles.slotParams}>
            <select aria-label="Slot kind" value={s.kind} onChange={(e) => changeKind(i, e.target.value)}>
              {SLOT_KINDS.map((k) => <option key={k} value={k}>{SLOT_KIND_LABEL[k]}</option>)}
            </select>
            {s.kind === 'repeat' && (
              <>
                <span>Every</span>
                <input
                  type="number"
                  min={RANGE.everyDays.min}
                  max={RANGE.everyDays.max}
                  step={1}
                  aria-label="Every N days"
                  aria-invalid={!!errors[s.id]}
                  value={raw[s.id] ?? s.everyDays}
                  onChange={(e) => changeCount(i, 'everyDays', 'everyDays', e.target.value)}
                />
                <span>days</span>
                {timeInput(s, i)}
              </>
            )}
            {s.kind === 'relative' && (
              <>
                <input
                  type="number"
                  min={RANGE[s.unit]?.min}
                  max={RANGE[s.unit]?.max}
                  step={1}
                  aria-label="Amount"
                  aria-invalid={!!errors[s.id]}
                  value={raw[s.id] ?? s.amount}
                  onChange={(e) => changeCount(i, 'amount', s.unit, e.target.value)}
                />
                <select aria-label="Unit" value={s.unit} onChange={(e) => changeUnit(i, e.target.value)}>
                  <option value="days">days</option>
                  <option value="hours">hours</option>
                </select>
                <select aria-label="Direction" value={s.direction} onChange={(e) => patch(i, { direction: e.target.value })}>
                  <option value="before">before</option>
                  <option value="after">after</option>
                </select>
                <select aria-label="Anchor" value={s.anchor} onChange={(e) => patch(i, { anchor: e.target.value })}>
                  {ANCHORS.map((a) => <option key={a} value={a}>{ANCHOR_LABEL[a]}</option>)}
                </select>
                {s.unit === 'days' && timeInput(s, i)}
              </>
            )}
            <button
              type="button"
              className={styles.iconBtn}
              aria-label="Remove slot"
              title="Remove slot"
              onClick={() => {
                clearRaw(s.id);
                onChange(slots.filter((_, j) => j !== i));
              }}
            >
              ✕
            </button>
          </div>
          {errors[s.id] && <div className={styles.error} role="alert">{errors[s.id]}</div>}
        </div>
      ))}
      {emptyError && <div className={styles.error} role="alert">{emptyError}</div>}
      <div className={styles.slotParams}>
        <select aria-label="New slot kind" value={addKind} onChange={(e) => setAddKind(e.target.value)}>
          {SLOT_KINDS.map((k) => <option key={k} value={k}>{SLOT_KIND_LABEL[k]}</option>)}
        </select>
        <button type="button" className={styles.secondaryBtn} onClick={() => onChange([...slots, newSlot(addKind, slots)])}>
          Add slot
        </button>
      </div>
    </div>
  );
}
